<template>
  <div class="creative-moderation">
    <header class="creative-moderation__header">
      <h1 class="creative-moderation__title">Модерация креативов</h1>
    </header>

    <section v-if="creatives.length === 0" class="creative-moderation__empty">
      <p>Нет креативов на модерации</p>
    </section>

    <section v-else class="creative-moderation__list">
      <article v-for="creative in creatives" :key="creative.id" class="moderation-card">
        <div class="moderation-card__main">
          <div class="moderation-card__titles">
            <h3 class="moderation-card__title" :title="creative.name">{{ creative.name }}</h3>
            <div class="moderation-card__meta-row">
              <span class="meta-id">#{{ creative.id }}</span>
              <span class="meta-type">{{ creative.type === 'picture' ? 'Изображение' : 'Видео' }}</span>
              <span class="meta-size">{{ creative.size || (creative.width && creative.height ?
                `${creative.width}x${creative.height}` : '') }}</span>
              <span class="meta-created">{{ formatDate(creative.created_at) }}</span>
            </div>
          </div>

          <div class="moderation-card__chips">
            <span v-if="creative.created_by" class="chip">{{ creative.created_by.name }}</span>
            <span v-else-if="creative.creator" class="chip">{{ creative.creator.name }}</span>
            <span v-if="creative.brand" class="chip">{{ brandLabel(creative) }}</span>
          </div>

          <div class="moderation-card__description" v-if="creative.description">
            {{ creative.description }}
          </div>
        </div>

        <div class="moderation-card__preview" v-if="creative.url">
          <img v-if="creative.type === 'picture'" :src="creative.url" :alt="creative.name" class="preview-thumb" />
          <video v-else :src="creative.url" class="preview-thumb" muted playsinline></video>
        </div>

        <div class="moderation-card__actions">
          <button @click="showApproveModal(creative)" class="act act--approve" :disabled="processingId === creative.id">
            {{ processingId === creative.id ? '...' : 'Подтвердить' }}
          </button>
          <button @click="showRejectModal(creative)" class="act act--reject" :disabled="processingId === creative.id">
            Отклонить
          </button>
        </div>
      </article>
    </section>

    <!-- Approve modal -->
    <div v-if="showApproveDialog" class="modal-overlay" @click.self="closeApproveModal">
      <div class="modal-content modal-content--wide">
        <p><strong>{{ creativeToApprove?.name }}</strong></p>
        <div class="form-group">
          <label for="api-id">API ID (обязательно):</label>
          <input id="api-id" type="number" v-model.number="apiId" class="form-control"
            placeholder="Введите API ID креатива" min="1" required />
          <small class="form-hint">API ID должен быть уникальным идентификатором креатива во внешней системе</small>
        </div>
        <div class="form-group">
          <label>Организации для загрузки (обязательно):</label>
          <small class="form-hint">Выберите одну или несколько организаций</small>
          <div class="organizations-list">
            <label v-for="org in organizations" :key="org.oid" class="organization-item"
              :class="{ 'organization-item--selected': selectedOids.includes(org.oid) }">
              <input type="checkbox" :value="org.oid" v-model="selectedOids" class="organization-checkbox" />
              <span class="organization-name">{{ org.name }}</span>
              <span class="organization-oid">({{ org.oid }})</span>
            </label>
          </div>
          <div v-if="organizations.length === 0" class="no-organizations">Нет доступных организаций</div>
        </div>
        <div class="modal-actions">
          <button @click="closeApproveModal" class="btn btn--secondary">Отмена</button>
          <button @click="approveCreative" class="btn btn--success" :disabled="!canApprove">Подтвердить{{
            selectedOids.length > 1 ? ` (${selectedOids.length} орг.)` : '' }}</button>
        </div>
      </div>
    </div>

    <!-- Reject modal -->
    <div v-if="showRejectDialog" class="modal-overlay" @click.self="closeRejectModal">
      <div class="modal-content">
        <h3>Отклонить креатив</h3>
        <p><strong>{{ creativeToReject?.name }}</strong></p>
        <div class="form-group">
          <label for="rejection-reason">Причина отклонения (необязательно):</label>
          <textarea id="rejection-reason" v-model="rejectionReason" class="form-control" rows="4"
            placeholder="Укажите причину отклонения креатива..."></textarea>
        </div>
        <div class="modal-actions">
          <button @click="closeRejectModal" class="btn btn--secondary">Отмена</button>
          <button @click="rejectCreative" class="btn btn--danger">Отклонить</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { formatInRtbTZ } from '../../../utils/timezones';

export default {
  name: 'CreativeModeration',
  props: {
    user: {
      type: Object,
      required: true,
    },
    organizations: {
      type: Array,
      default: () => [],
    },
    initialCreatives: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      creatives: [],
      processingId: null,
      showApproveDialog: false,
      creativeToApprove: null,
      apiId: null,
      selectedOids: [],
      showRejectDialog: false,
      creativeToReject: null,
      rejectionReason: '',
      syncing: false,
    };
  },
  computed: {
    canApprove() {
      return this.apiId && this.selectedOids.length > 0;
    },
  },
  mounted() {
    this.creatives = [...this.initialCreatives];
  },
  methods: {
    brandLabel(creative) {
      if (!creative || !creative.brand) return '';
      if (typeof creative.brand === 'object') return creative.brand.name || '';
      return creative.brand;
    },
    async syncStatuses() {
      if (this.syncing) return;
      this.syncing = true;
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        const res = await fetch('/api/creatives/sync-statuses', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken,
          },
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (data.success) {
          this.$notify({
            title: 'Успешно',
            message: `Обновлено креативов: ${data.creatives_updated || 0}`,
            type: 'success',
          });
        }
      } catch (e) {
        this.$notify({
          title: 'Ошибка',
          message: 'Не удалось обновить статусы',
          type: 'error',
        });
      } finally {
        this.syncing = false;
      }
    },
    showApproveModal(creative) {
      this.creativeToApprove = creative;
      this.apiId = null;
      this.selectedOids = [];
      // Если есть только одна организация, выбираем её по умолчанию
      if (this.organizations.length === 1) {
        this.selectedOids = [this.organizations[0].oid];
      }
      this.showApproveDialog = true;
    },
    closeApproveModal() {
      this.showApproveDialog = false;
      this.creativeToApprove = null;
      this.apiId = null;
      this.selectedOids = [];
    },
    async approveCreative() {
      if (!this.creativeToApprove || !this.apiId || this.selectedOids.length === 0) return;

      this.processingId = this.creativeToApprove.id;
      try {
        const response = await axios.post(`/api/creatives/${this.creativeToApprove.id}/approve`, {
          api_id: this.apiId,
          oids: this.selectedOids,
        });

        const successCount = response.data.results?.filter(r => r.success).length || this.selectedOids.length;
        const failCount = response.data.results?.filter(r => !r.success).length || 0;

        if (failCount > 0) {
          this.$notify({
            title: 'Частично успешно',
            message: `Креатив загружен в ${successCount} из ${this.selectedOids.length} организаций`,
            type: 'warning',
          });
        } else {
          this.$notify({
            title: 'Успешно',
            message: response.data.message || `Креатив загружен в ${successCount} организаций`,
            type: 'success',
          });
        }

        // Удаляем креатив из списка
        this.creatives = this.creatives.filter(c => c.id !== this.creativeToApprove.id);
        this.closeApproveModal();
      } catch (error) {
        this.$notify({
          title: 'Ошибка',
          message: error.response?.data?.error || 'Не удалось подтвердить креатив',
          type: 'error',
        });
      } finally {
        this.processingId = null;
      }
    },
    showRejectModal(creative) {
      this.creativeToReject = creative;
      this.rejectionReason = '';
      this.showRejectDialog = true;
    },
    closeRejectModal() {
      this.showRejectDialog = false;
      this.creativeToReject = null;
      this.rejectionReason = '';
    },
    async rejectCreative() {
      if (!this.creativeToReject) return;

      this.processingId = this.creativeToReject.id;
      try {
        const response = await axios.post(`/api/creatives/${this.creativeToReject.id}/reject`, {
          rejection_reason: this.rejectionReason,
        });

        this.$notify({
          title: 'Успешно',
          message: response.data.message || 'Креатив отклонен',
          type: 'success',
        });

        // Удаляем креатив из списка
        this.creatives = this.creatives.filter(c => c.id !== this.creativeToReject.id);
        this.closeRejectModal();
      } catch (error) {
        this.$notify({
          title: 'Ошибка',
          message: error.response?.data?.error || 'Не удалось отклонить креатив',
          type: 'error',
        });
      } finally {
        this.processingId = null;
      }
    },
    formatDate(dateString) {
      return formatInRtbTZ(dateString);
    },
    formatDuration(durationMs) {
      if (!durationMs) return '';
      const seconds = Math.round(durationMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes > 0) {
        return `${minutes}м ${remainingSeconds}с`;
      }
      return `${remainingSeconds}с`;
    },
  },
};
</script>